/**
 * Debounces a method so only the last call made within the delay window executes.
 *
 * Timers are tracked per instance to avoid cross-instance interference when the
 * same decorated method exists on multiple component or service instances.
 *
 * @param ms - The delay in milliseconds before invoking the original method
 */
export function debounce(ms: number) {
  const timers = new WeakMap<object, ReturnType<typeof setTimeout>>();

  return <TThis extends object, TArgs extends unknown[]>(
    _target: TThis,
    _propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<(this: TThis, ...args: TArgs) => void>
  ): void => {
    const originalMethod = descriptor.value;

    if (!originalMethod) {
      return;
    }

    descriptor.value = function (this: TThis, ...args: TArgs): void {
      const existingTimer = timers.get(this);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      const timeoutId = setTimeout(() => {
        originalMethod.apply(this, args);
        timers.delete(this);
      }, ms);

      timers.set(this, timeoutId);
    };
  };
}

/**
 * Throttles a method so the first call executes immediately and subsequent
 * calls within the throttle window are ignored.
 *
 * Timers are tracked per instance to avoid cross-instance interference when the
 * same decorated method exists on multiple component or service instances.
 *
 * @param ms - The throttle window in milliseconds (default: 700)
 * @param debug - When true, logs suppressed calls for debugging (default: false)
 */
export function throttle(ms = 700, debug = false) {
  const timers = new WeakMap<object, ReturnType<typeof setTimeout>>();

  return <TThis extends object, TArgs extends unknown[]>(
    _target: TThis,
    _propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<(this: TThis, ...args: TArgs) => void>
  ): void => {
    const originalMethod = descriptor.value;

    if (!originalMethod) {
      return;
    }

    descriptor.value = function (this: TThis, ...args: TArgs): void {
      if (timers.has(this)) {
        if (debug) {
          console.warn(
            `Throttled method "${String(
              _propertyKey
            )}" called too frequently. Ignoring subsequent calls within ${ms}ms window.`
          );
        }
        return;
      }

      originalMethod.apply(this, args);

      const timeoutId = setTimeout(() => {
        timers.delete(this);
      }, ms);

      timers.set(this, timeoutId);
    };
  };
}
